import { defineStore } from 'pinia';
import { useCartStore } from './cart';
import { useUiStore } from './ui';
import { DeliveryId, useDeliveryStore } from './delivery';
import { useStoresStore } from './stores';
import type { CheckoutOrder, ExtendedItem, ExtendedOrder, Item } from '@/types/order';
import { Price } from '~/types/products';
import { Checkout } from '~/plugins/services/cart/types';

export const useOrderStore = defineStore('order', () => {
  const orders = ref<ExtendedOrder[]>([]);
  const priceList = ref<Price[]>([]);
  const isPlacingOrder = ref<boolean>(false);

  const clearState = (): void => {
    orders.value = [];
    priceList.value = [];
  };

  const setIsPlacingOrder = (placingOrder: boolean): void => {
    isPlacingOrder.value = placingOrder;
  };

  const checkoutCart = async (payload: Checkout) => {
    const { getPrices, fetchCart } = useCartStore();
    const { showMessage } = useUiStore();
    const { $cartService, $router, $i18n } = useNuxtApp();
    try {
      setIsPlacingOrder(true);

      const { data: orders, error } = await $cartService.checkout(payload);
      const placedOrders = orders?.filter(({ success }: { success: boolean }) => success) || [];

      if (error || !placedOrders.length) throw new Error('Error placing order');
      if (orders!.length > placedOrders.length) {
        showPartialOrderErrorMessage(orders!);
      }
      priceList.value = getPrices;
      setExtendedOrderList(placedOrders);
      await fetchCart();
      $router.push({ name: 'checkout-success-page' });
    } catch (error) {
      clearState();
      showMessage({ text: $i18n.t('errors.invalid_order') });
      await fetchCart();
      await $router.push({ name: 'index' });
      setIsPlacingOrder(false);
    }
  };

  const punchout = async ({
    cartId,
    url,
    form,
  }: {
    cartId: number;
    url: string;
    form: HTMLFormElement;
  }): Promise<void> => {
    const { showMessage } = useUiStore();
    const { fetchCart } = useCartStore();
    const { $cartService, $i18n } = useNuxtApp();
    setIsPlacingOrder(true);
    try {
      form.method = 'POST';
      form.action = url;
      form.submit();
    } catch (error) {
      showMessage({ text: $i18n.t('errors.internal_server_error') });
    } finally {
      $cartService.deleteCart({ cartId });
      setIsPlacingOrder(false);
      await fetchCart();
    }
  };

  const showPartialOrderErrorMessage = (order: CheckoutOrder[]): void => {
    const { getDeliveryNameFromId } = useDeliveryStore();
    const { showMessage } = useUiStore();
    const { $i18n } = useNuxtApp();
    const unsuccessfullOrders = order
      .filter(({ success }) => !success)
      .map(({ deliveryId }) => getDeliveryNameFromId(deliveryId).value)
      .join();

    showMessage({ text: $i18n.t('errors.invalid_partial_order', { unsuccessfullOrders }) });
  };

  const setExtendedOrderList = (newOrders: CheckoutOrder[]): void => {
    const { getDeliveryNameFromId, selectedLocations } = useDeliveryStore();
    const { storeIdMap } = useStoresStore();
    const { getCartItemsObject } = useCartStore();
    const { $tracking, $i18n } = useNuxtApp();

    const extendedOrders = newOrders.map((order: CheckoutOrder): ExtendedOrder => {
      const extendedOrderItems = order.orderItems.map((orderItem: Item): ExtendedItem => {
        return { ...orderItem, ...getCartItemsObject[orderItem.cartItemId!] };
      });

      return {
        ...order,
        deliveryName: getDeliveryNameFromId(order.deliveryId).value,
        orderItems: extendedOrderItems,
      };
    });
    orders.value = extendedOrders;

    extendedOrders.forEach((order) => {
      if (!order.tempOrderId || order.orderItems.length === 0) return;

      const locationId = selectedLocations[order.deliveryId as DeliveryId];
      const locationName = storeIdMap[locationId]?.name;

      const affiliation = order.orderDetails?.warehouseId ? locationName : $i18n.t('central_stock').toLowerCase();

      $tracking.emit('purchase', order.tempOrderId, order.orderItems, affiliation);
    });
  };

  return {
    orders,
    priceList,
    isPlacingOrder,
    clearState,
    setIsPlacingOrder,
    checkoutCart,
    punchout,
  };
});
